import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// Extend dayjs with UTC plugin
dayjs.extend(utc);

const LOCALES = {
  en: {
    monthNames: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ],
    texts: {
      justNow: 'Just now',
      minuteAgo: (n: number) => n === 1 ? '1 minute ago' : `${n} minutes ago`,
      hourAgo: (n: number) => n === 1 ? '1 hour ago' : `${n} hours ago`
    },
    format: (day: number, month: number, year: number) => `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`,
    // Date format patterns for DatePicker (using Day.js format)
    dateFormat: 'MM/DD/YYYY',
    dateTimeFormat: 'MM/DD/YYYY HH:mm:ss',
    dateTimeShortFormat: 'MM/DD/YYYY HH:mm'
  },
  es: {
    monthNames: [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ],
    texts: {
      justNow: 'Ahora mismo',
      minuteAgo: (n: number) => n === 1 ? 'Hace 1 minuto' : `Hace ${n} minutos`,
      hourAgo: (n: number) => n === 1 ? 'Hace 1 hora' : `Hace ${n} horas`
    },
    format: (day: number, month: number, year: number) => `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`,
    // Date format patterns for DatePicker (using Day.js format)
    dateFormat: 'DD/MM/YYYY',
    dateTimeFormat: 'DD/MM/YYYY HH:mm:ss',
    dateTimeShortFormat: 'DD/MM/YYYY HH:mm'
  }
} as const;

type Language = 'en' | 'es';


const parseDate = (dateString: string, language: Language = 'es'): Date => {
  if (!dateString) {
    return new Date();
  }

  // Try ISO format first (preferred for backend data)
  if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)?$/.test(dateString)) {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  // Try locale-specific formats
  const patterns = [
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, // dd/MM/yyyy or MM/dd/yyyy
    /^(\d{4})-(\d{1,2})-(\d{1,2})$/ // yyyy-MM-dd
  ];

  for (const pattern of patterns) {
    const match = dateString.match(pattern);
    if (match) {
      const [, first, second, third] = match.map(Number);

      // Determine if it's ISO format (yyyy-MM-dd) or locale format
      const isISO = first > 31;
      let year: number, month: number, day: number;

      if (isISO) {
        [year, month, day] = [first, second - 1, third];
      } else {
        // Use language preference for day/month order
        const isDDMM = language === 'es';
        year = third;
        month = (isDDMM ? second : first) - 1;
        day = isDDMM ? first : second;
      }

      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }

  console.warn(`Unable to parse date: ${dateString}. Using current date.`);
  return new Date();
};

/**
 * Core function that formats a Date object into a relative date string
 * This function contains the shared logic used by both public functions
 */
const formatRelativeDate = (date: Date, language: Language = 'en'): string => {
  const now = new Date();
  const locale = LOCALES[language];

  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  // Within an hour
  if (diffMinutes < 60) {
    return diffMinutes <= 0 ? locale.texts.justNow : locale.texts.minuteAgo(diffMinutes);
  }

  // Within a day
  if (diffHours < 24) {
    return locale.texts.hourAgo(diffHours);
  }

  // Same month and year
  if (date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth()) {
    return `${locale.monthNames[date.getMonth()]} ${date.getDate()}`;
  }

  // Different month or year - use locale format
  return locale.format(date.getDate(), date.getMonth() + 1, date.getFullYear());
};

/**
 * Formats a date string into a relative date string with internationalization support
 */
export const formatRelativeDateI18n = (
  dateString: string,
  language: Language = 'en'
): string => {
  const date = parseDate(dateString, language);
  return formatRelativeDate(date, language);
};

/**
 * Formats a timestamp (number) into a relative date string with internationalization support
 */
export const formatRelativeDateFromTimestamp = (
  timestamp: number,
  language: Language = 'en'
): string => {
  const date = new Date(timestamp);

  // Validate the timestamp
  if (isNaN(date.getTime())) {
    console.warn(`Invalid timestamp: ${timestamp}. Using current date.`);
    return LOCALES[language].texts.justNow;
  }

  return formatRelativeDate(date, language);
};

/**
 * Get the date format pattern for a specific locale
 */
export const getDateFormat = (language: Language = 'en'): string => {
  return LOCALES[language].dateFormat;
};

/**
 * Get the date-time format pattern for a specific locale
 */
export const getDateTimeFormat = (language: Language = 'en'): string => {
  return LOCALES[language].dateTimeFormat;
};

/**
 * Get the short date-time format pattern for a specific locale
 */
export const getDateTimeShortFormat = (language: Language = 'en'): string => {
  return LOCALES[language].dateTimeShortFormat;
};

/**
 * Converts a date from DatePicker to a UTC timestamp for backend storage.
 * IMPORTANT: The date is treated as UTC directly (no timezone conversion).
 * If user selects "2025-10-21", it will be stored as "2025-10-21 00:00:00 UTC".
 *
 * @param date - Day.js date object from DatePicker
 * @returns Numeric timestamp in UTC (milliseconds), or null if date is invalid
 *
 * @example
 * // User selects "2025-10-21" in DatePicker
 * const timestamp = convertToUTCTimestamp(dayjs('2025-10-21'));
 * // Returns timestamp for "2025-10-21 00:00:00 UTC" (no timezone conversion)
 */
export const convertToUTCTimestamp = (date: any): number | null => {
  if (!date) {
    return null;
  }

  try {
    // If it's a Day.js object
    if (date && typeof date.valueOf === 'function') {
      // Get the year, month, day, hour, minute, second from the dayjs object
      // and create a UTC timestamp using Date.UTC()
      // This treats the selected date/time as UTC, not local time
      const timestamp = Date.UTC(
        date.year(),
        date.month(),
        date.date(),
        date.hour(),
        date.minute(),
        date.second(),
        date.millisecond()
      );
      return isNaN(timestamp) ? null : timestamp;
    }

    // If it's a Date object
    if (date instanceof Date) {
      // For Date objects, use UTC methods to avoid timezone conversion
      const timestamp = Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
      );
      return isNaN(timestamp) ? null : timestamp;
    }

    // If it's already a number
    if (typeof date === 'number') {
      return isNaN(date) ? null : date;
    }

    // If it's a string, try to parse it
    if (typeof date === 'string') {
      const parsedDate = new Date(date);
      const timestamp = parsedDate.getTime();
      return isNaN(timestamp) ? null : timestamp;
    }

    return null;
  } catch (error) {
    console.error('Error converting date to UTC timestamp:', error);
    return null;
  }
};

/**
 * Converts a UTC timestamp to a dayjs object for display in DatePicker.
 * IMPORTANT: The timestamp is treated as UTC and displayed as-is (no timezone conversion).
 * If backend sends "2025-10-21 00:00:00 UTC", it will be displayed as "2025-10-21".
 *
 * @param timestamp - UTC timestamp in milliseconds from backend
 * @returns Day.js object representing the UTC date/time, or null if invalid
 *
 * @example
 * // Backend sends UTC timestamp for "2025-10-21 00:00:00 UTC"
 * // User will see "2025-10-21" in their DatePicker (same date, no conversion)
 * const localDate = convertUTCTimestampToLocal(1729468800000);
 */
export const convertUTCTimestampToLocal = (timestamp: number): any => {
  if (!timestamp || isNaN(timestamp)) {
    return null;
  }

  try {
    // Create a Date object from the UTC timestamp
    const utcDate = new Date(timestamp);

    // Extract the UTC components
    const year = utcDate.getUTCFullYear();
    const month = utcDate.getUTCMonth();
    const day = utcDate.getUTCDate();
    const hour = utcDate.getUTCHours();
    const minute = utcDate.getUTCMinutes();
    const second = utcDate.getUTCSeconds();
    const millisecond = utcDate.getUTCMilliseconds();

    // Create a dayjs object using the UTC components as local values
    // This displays the UTC date/time without timezone conversion
    return dayjs(new Date(year, month, day, hour, minute, second, millisecond));
  } catch (error) {
    console.error('Error converting UTC timestamp to local date:', error);
    return null;
  }
};
